import { Circle } from "lucide-react";
import { Select, SelectContent, SelectTrigger } from "./select";

interface BrushSizeSelectorProps {
    value: number;
    onChange: (size: number) => void;
    disabled?: boolean;
    className?: string;
}

const getVisualDotSize = (strokeWidth: number): number => {
    // More accurate scaling to match actual canvas stroke appearance
    // Small strokes (1-5): scale more aggressively for visibility
    // Large strokes (15-20): scale less to fit in UI
    if (strokeWidth <= 5) {
        return Math.max(strokeWidth * 1.2, 4);
    } else if (strokeWidth <= 10) {
        return strokeWidth * 1.0;
    } else {
        return Math.min(strokeWidth * 0.8, 18);
    }
};

export function BrushSizeSelector({ value, onChange, disabled, className }: BrushSizeSelectorProps) {
    return (
        <Select
            value={value.toString()}
            onValueChange={(newValue: string) => onChange(Number(newValue))}
            disabled={disabled}
        >
            <SelectTrigger className={`w-32 cursor-pointer ${className || ""}`}>
                <div className="flex items-center gap-2 justify-center">
                    <span className="text-xs text-muted-foreground">Size</span>
                    <div className="w-4 h-4 flex items-center justify-center">
                        <Circle
                            className="fill-current"
                            style={{
                                width: `${getVisualDotSize(value)}px`,
                                height: `${getVisualDotSize(value)}px`,
                            }}
                        />
                    </div>
                </div>
            </SelectTrigger>
            <SelectContent className="w-48">
                <div className="p-3 space-y-3">
                    {/* Quick preset buttons with visual size indicators */}
                    <div className="flex gap-2 justify-center">
                        <button
                            onClick={() => onChange(3)}
                            className={`flex items-center justify-center w-12 h-8 rounded border cursor-pointer ${
                                value === 3
                                    ? "border-primary bg-primary/10 ring-1 ring-primary"
                                    : "border-border hover:bg-primary/10 hover:text-primary"
                            }`}
                            aria-label="Small brush size"
                        >
                            <Circle className="h-2 w-2 fill-current" />
                        </button>
                        <button
                            onClick={() => onChange(8)}
                            className={`flex items-center justify-center w-12 h-8 rounded border cursor-pointer ${
                                value === 8
                                    ? "border-primary bg-primary/10 ring-1 ring-primary"
                                    : "border-border hover:bg-primary/10 hover:text-primary"
                            }`}
                            aria-label="Medium brush size"
                        >
                            <Circle className="h-3 w-3 fill-current" />
                        </button>
                        <button
                            onClick={() => onChange(15)}
                            className={`flex items-center justify-center w-12 h-8 rounded border cursor-pointer ${
                                value === 15
                                    ? "border-primary bg-primary/10 ring-1 ring-primary"
                                    : "border-border hover:bg-primary/10 hover:text-primary"
                            }`}
                            aria-label="Large brush size"
                        >
                            <Circle className="h-4 w-4 fill-current" />
                        </button>
                    </div>
                    {/* Fine control slider */}
                    <div className="space-y-2">
                        <label className="text-xs text-muted-foreground">Size: {value}px</label>
                        <input
                            type="range"
                            min="1"
                            max="20"
                            value={value}
                            onChange={(e) => onChange(Number(e.target.value))}
                            className="w-full h-2 bg-muted rounded-lg cursor-pointer appearance-none focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                    </div>
                </div>
            </SelectContent>
        </Select>
    );
}
